import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, Heart, Clock, Music, Settings, BookOpen, Download } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { OnboardingModal, useOnboarding } from '@/components/OnboardingModal';
import { useApp } from '@/contexts/AppContext';

const BASE_URL = 'https://jempwa.croney-tech.fr';

const menuItems = [
  {
    path: '/search',
    icon: Search,
    label: 'Recherche',
    description: 'Trouver un chant',
    color: 'bg-blue-500 hover:bg-blue-600'
  },
  {
    path: '/favorites',
    icon: Heart,
    label: 'Favoris',
    description: 'Mes chants préférés',
    color: 'bg-red-500 hover:bg-red-600'
  },
  {
    path: '/history',
    icon: Clock,
    label: 'Historique',
    description: 'Chants récents',
    color: 'bg-purple-500 hover:bg-purple-600'
  },
  {
    path: '/player',
    icon: Music,
    label: 'Lecteur',
    description: 'Écouter un chant',
    color: 'bg-green-500 hover:bg-green-600'
  },
  {
    path: '/browse',
    icon: BookOpen,
    label: 'Catalogue',
    description: 'Parcourir tout',
    color: 'bg-indigo-500 hover:bg-indigo-600'
  },
  {
    path: '/offline',
    icon: Download,
    label: 'Hors ligne',
    description: 'Téléchargements',
    color: 'bg-teal-500 hover:bg-teal-600'
  },
  {
    path: '/settings',
    icon: Settings,
    label: 'Paramètres',
    description: 'Configuration',
    color: 'bg-gray-500 hover:bg-gray-600'
  },
  {
    path: '/donate',
    icon: Heart,
    label: 'Soutenir',
    description: 'Faire un don',
    color: 'bg-gradient-to-br from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700'
  },
];

export function HomePage() {
  const { favorites, history } = useApp();
  const { showOnboarding, completeOnboarding } = useOnboarding();

  return (
    <Layout title="Accueil" showBack={false}>
      <Helmet>
        <title>Chants JEM - Jeunesse en Mission | Paroles et Partitions</title>
        <meta name="description" content="Application complète des chants JEM (Jeunesse en Mission). Accédez aux paroles, partitions PDF, fichiers MIDI de plus de 1000 chants chrétiens." />
        <link rel="canonical" href={BASE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Chants JEM - Jeunesse en Mission" />
        <meta property="og:description" content="Application complète des chants JEM. Paroles, partitions et MIDI." />
        <meta property="og:url" content={BASE_URL} />
        <meta property="og:site_name" content="Chants JEM" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Chants JEM - Jeunesse en Mission" />
      </Helmet>
      <div className="max-w-4xl mx-auto">
        {/* En-tête */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-2">Chants JEM</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Jeunesse en Mission - Collection complète
          </p>
        </div>

        {/* Grille de boutons carrés */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {menuItems.map(({ path, icon: Icon, label, description, color }) => (
            <Link
              key={path}
              to={path}
              className={`
                ${color} text-white rounded-xl shadow-lg
                aspect-square flex flex-col items-center justify-center
                p-6 transition-all duration-200 transform hover:scale-105
                focus:outline-none focus:ring-4 focus:ring-blue-300
              `}
              aria-label={`${label} - ${description}`}
            >
              <Icon className="w-12 h-12 mb-3" />
              <span className="font-semibold text-lg mb-1">{label}</span>
              <span className="text-xs opacity-90">{description}</span>
            </Link>
          ))}
        </div>

        {/* Statistiques rapides */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow text-center">
            <div className="text-2xl font-bold text-blue-600">1199</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Chants</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow text-center">
            <div className="text-2xl font-bold text-red-600">{favorites.length}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Favoris</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow text-center">
            <div className="text-2xl font-bold text-purple-600">{history.length}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Récents</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow text-center">
            <div className="text-2xl font-bold text-green-600">4</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Volumes</div>
          </div>
        </div>
      </div>

      {/* Onboarding modal */}
      {showOnboarding && (
        <OnboardingModal onComplete={completeOnboarding} />
      )}
    </Layout>
  );
}
